import {
  Cart,
  CartConstantsEnum,
  CartEvents,
  CartEventsEnum,
  cartApi,
  CART_TYPE,
  CartEntry,
  updateShippingAddress,
} from '@commerce-cart-webcomponents/sdk';
import {
  SubscriptionElement,
  SubscriptionResponse,
  SubcriptionPlanCMSResponseType,
} from '@commerce-subscription-webcomponents/sdk';
import type { AppState } from '@nextgen-web-framework/core';
import { StorageService, Logger } from '@nextgen-web-framework/core';
import { useNextgenRouter } from '@nextgen-web-framework/all';
import { useAddressUpdate } from '@commerce-webcomponents/ui';
import { OrderType } from '../EnableGroupOrder/types/propTypes';
import { getCurrentElementFromSubscription } from '../../../utils/utils';
import logObject from '../../../../logObject';
import { groupProductByAvailableVariants } from '../../../utils/variantsProductUtils';
import { UpdateCartDetailsPropTypes } from './types/propTypes';

const useUpdateCartDetails = ({
  enableGroupOrder,
  cartId,
  cartVersion,
  subCart,
  // TODO: Enable this code for GCO Mode
  // currentOrderType,
  subscriptionsCmsData,
  onSuccess,
  onFail,
  newOrderType,
}: UpdateCartDetailsPropTypes) => {
  const { push } = useNextgenRouter();
  const loggerServiceInstance = Logger.getInstance(logObject('useUpdateCartDetails'));
  const config = ConfigService.getConfigs();
  const SOP_VARIANT_SHOW_IN_CART_ENABLED = config.NEXT_SOP_VARIANT_SHOW_IN_CART_ENABLED;
  const { updateAddress } = useAddressUpdate();

  const getPlanImages = () => {
    if (subscriptionsCmsData.length && subscriptionsCmsData[0].plan_image?.images) {
      return subscriptionsCmsData[0].plan_image?.images.map((image: AppState) => ({
        viewport: image?.viewport,
        imageUrl: image?.image?.url,
      }));
    }
    return [];
  };

  const handleSuccess = (subscriptionDetails: SubscriptionResponse, cartRes?: Cart, event?: string) => {
    enableGroupOrder &&
      updateShippingAddress(
        subscriptionDetails?.amwayUser?.userId as string,
        subscriptionDetails?.amwayUser?.accountId as string,
        updateAddress,
      );
    if (onSuccess) {
      onSuccess();
    } else {
      push && push(`/subscription-confirmation/${subscriptionDetails.id}`);
    }
    // Updates mini cart count
    event &&
      CartEvents.publishEvent({
        event: CartEventsEnum.AddToCart,
        payload: {
          cart: cartRes,
          cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          cart_type: cartRes?.orderType === OrderType.Gco ? CART_TYPE.GROUP : CART_TYPE.PERSONAL,
        },
      });
  };

  const handleFailure = (subscriptionDetails: SubscriptionResponse, error: unknown, createCartError = false) => {
    loggerServiceInstance.error(createCartError ? 'Error in create cart api' : 'Error in add to cart api', error);
    if (onFail) {
      onFail();
    } else {
      push && push(`/subscription-confirmation/${subscriptionDetails.id}?isErrorInUpdateCart=true`);
    }
  };

  const getEntries = (subscriptionDetails: SubscriptionResponse) => {
    const currentElement = getCurrentElementFromSubscription(subscriptionDetails);
    const { productVariantMap } = groupProductByAvailableVariants(subscriptionDetails);
    const couponDetailsWithBenefit =
      currentElement && currentElement?.userChoiceBenefit && currentElement?.userChoiceBenefit[0];
    const subscriptionEntries =
      subscriptionDetails?.currentElements?.flatMap(
        (currentElement: SubscriptionElement) =>
          currentElement?.entries?.map(
            (entry) =>
              ({
                ...entry,
                product: {
                  ...entry.product,
                  id: entry?.product?.id ?? '',
                },
                sopElementId: currentElement?.id,
                ...(currentElement?.allowChangeProductVariant
                  ? {
                      allowProductVariantChange:
                        SOP_VARIANT_SHOW_IN_CART_ENABLED &&
                        currentElement?.allowChangeProductVariant &&
                        productVariantMap[entry?.product?.id || ''] > 1
                          ? currentElement?.allowChangeProductVariant
                          : false,
                    }
                  : {}),
              } as CartEntry),
          ) ?? [],
      ) || [];

    const benefitCouponEntries =
      couponDetailsWithBenefit?.entries?.map(
        (entry) =>
          ({
            ...entry,
            coupons: [couponDetailsWithBenefit?.coupon],
            partnerPromotionId: couponDetailsWithBenefit?.promotionId,
          } as CartEntry),
      ) ?? [];
    const allEntries = [...subscriptionEntries, ...benefitCouponEntries];
    return allEntries;
  };

  const createSopMetaData = (
    subscriptionDetails: SubscriptionResponse,
    subscriptionsCmsData: SubcriptionPlanCMSResponseType[],
  ) =>
    subscriptionDetails?.currentElements?.map((currentElement) => ({
      sopElementId: currentElement?.id ?? '',
      sopId: subscriptionDetails.id,
      sopCode: subscriptionDetails.code,
      sopPlanId: subscriptionDetails.plan?.id,
      planName: subscriptionsCmsData.length ? subscriptionsCmsData[0].plan_name : '',
      images: getPlanImages(), // Assuming getPlanImages() exists elsewhere
      elementSequence: currentElement?.elementSequence || 1,
      totalShipments: subscriptionDetails.currentCycleTotalShipments,
      isCompensated: currentElement?.isCompensated,
      isPostponed: currentElement?.isPostponed,
      coupons: currentElement?.coupons?.length ? currentElement?.coupons : undefined,
      promotionStacking: currentElement?.coupons?.length ? true : currentElement?.isPlanStackable,
      purchaseByDate: subscriptionDetails.nextPaymentDate, // @TODO
      isFreeDelivery: currentElement?.isFreeDelivery,
      planCode: subscriptionDetails.plan?.code,
      version: currentElement?.version || 0,
    }));

  const addToCart = async (subscriptionDetails: SubscriptionResponse) => {
    let cartVersionForPayload = cartVersion;

    try {
      /* TODO: Enable this code for GCO Mode
      if (enableGroupOrder) {
        const updateOrderType = (await cartApi.updateGCOFlag({
          cartId,
          subCartId: subCart?.subCartId,
          payload: {
            orderType: enableGroupOrder ? OrderType.Gco : currentOrderType,
            version: cartVersion,
          },
        })) as Cart;

        if (updateOrderType?.version) {
          cartVersionForPayload = updateOrderType?.version;
        }
      } */

      if (enableGroupOrder) {
        const updateOrderType = (await cartApi.updateGCOFlag({
          cartId,
          subCartId: subCart?.subCartId,
          payload: {
            orderType: newOrderType,
            version: cartVersion,
          },
        })) as Cart;

        if (updateOrderType?.version) {
          cartVersionForPayload = updateOrderType?.version;
        }
      }

      const payload = {
        // TODO: Enable this code for GCO Mode
        // orderType: enableGroupOrder ? OrderType.Gco : currentOrderType,
        orderType: newOrderType,
        subCarts: [
          {
            subCartId: subCart?.subCartId,
            entries: getEntries(subscriptionDetails),
            sopMetaData: createSopMetaData(subscriptionDetails, subscriptionsCmsData),
          },
        ],
        version: cartVersionForPayload,
      };

      const response = (await cartApi.addToCart({
        cartId,
        subCartId: subCart?.subCartId,
        payload,
      })) as Cart;

      if (response) {
        handleSuccess(subscriptionDetails, response, CartEventsEnum.AddToCart);
      }
    } catch (error) {
      handleFailure(subscriptionDetails, error);
    }
  };

  const createCart = async (subscriptionDetails: SubscriptionResponse) => {
    const payload = {
      amwayUser: {
        amwayUserId: subscriptionDetails.amwayUser?.userId,
        accountId: subscriptionDetails.amwayUser?.accountId,
      },
      entries: getEntries(subscriptionDetails),
      // TODO: Enable this code for GCO Mode
      // orderType: enableGroupOrder ? OrderType.Gco : currentOrderType,
      orderType: newOrderType,
      sopMetaData: createSopMetaData(subscriptionDetails, subscriptionsCmsData),
      version: cartVersion,
    };

    try {
      const response = (await cartApi.createSubCart({
        cartId,
        payload,
      })) as Cart;
      if (response) {
        handleSuccess(subscriptionDetails);
      }
    } catch (error) {
      handleFailure(subscriptionDetails, error, true);
    }
  };

  return { addToCart, createCart };
};

export { useUpdateCartDetails };
